import { ActionEvents } from '@griply/types';
import { lifeAreaToTrackingLifeAreaType } from '@griply/utils';
import { useCallback, useRef } from 'react';
import { useDeleteLifeAreaMutation } from 'shared/hooks/queries/use-delete-life-area-mutation';
import { useDeleteFavorites } from 'shared/hooks/use-delete-favorites';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { LifeArea } from 'shared/types/life-area';

export const useDeleteLifeArea = () => {
  const deleteLifeAreasRef = useRef<LifeArea[]>([]);

  const track = useTrackEvents();
  const { mutate, isSuccess, reset, error, isPending } =
    useDeleteLifeAreaMutation();
  const removeFavorite = useDeleteFavorites();

  const deleteAll = useCallback(
    (lifeAreas: LifeArea[]) => {
      lifeAreas.forEach(({ id }) => mutate(id));
      removeFavorite(lifeAreas.map(({ id }) => id));
    },
    [mutate, removeFavorite],
  );

  const submit = useCallback(
    (lifeAreas: LifeArea | LifeArea[]) => {
      const toDelete = Array.isArray(lifeAreas) ? lifeAreas : [lifeAreas];
      deleteLifeAreasRef.current = toDelete;

      toDelete.forEach((lifeArea) => {
        track(ActionEvents.LifeAreaDelete, {
          life_area: lifeAreaToTrackingLifeAreaType(lifeArea)!,
        });
      });

      deleteAll(toDelete);
    },
    [deleteAll, track],
  );

  const retry = useCallback(
    () => deleteAll(deleteLifeAreasRef.current),
    [deleteAll],
  );

  const resetAll = useCallback(() => {
    reset();
    deleteLifeAreasRef.current = [];
  }, [reset]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
